/**
 * BrightPath - ExpenseImportModal Component
 * BP-04-04: Expenses Frontend
 */

import React, { useState, useCallback } from 'react';
import { Upload, FileText, AlertCircle, CheckCircle, X } from 'lucide-react';
import type { ImportPreview, ImportResult, ExpenseCategory } from '../types';
import { EXPENSE_CATEGORIES } from '../types';
import { cn } from '@/core/lib/utils';

interface ExpenseImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPreview: (file: File) => Promise<ImportPreview>;
  onConfirm: (file: File, options?: { skipDuplicates?: boolean; defaultCategory?: string }) => Promise<ImportResult>;
  isLoading?: boolean;
}

export function ExpenseImportModal({
  isOpen,
  onClose,
  onPreview,
  onConfirm,
  isLoading = false,
}: ExpenseImportModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [defaultCategory, setDefaultCategory] = useState<ExpenseCategory>('other');
  const [step, setStep] = useState<'upload' | 'preview' | 'result'>('upload');

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile && droppedFile.type === 'text/csv') {
        setFile(droppedFile);
        setError(null);
        try {
          const previewResult = await onPreview(droppedFile);
          setPreview(previewResult);
          setStep('preview');
        } catch (err) {
          setError('Failed to parse CSV file. Please check the format.');
        }
      } else {
        setError('Please upload a CSV file');
      }
    },
    [onPreview]
  );

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (selectedFile) {
        setFile(selectedFile);
        setError(null);
        try {
          const previewResult = await onPreview(selectedFile);
          setPreview(previewResult);
          setStep('preview');
        } catch (err) {
          setError('Failed to parse CSV file. Please check the format.');
        }
      }
    },
    [onPreview]
  );

  const handleConfirm = async () => {
    if (!file) return;
    try {
      const importResult = await onConfirm(file, { skipDuplicates, defaultCategory });
      setResult(importResult);
      setStep('result');
    } catch (err) {
      setError('Failed to import expenses');
    }
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
    setStep('upload');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-navy/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-warm-gray/10 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-navy">Import Expenses from CSV</h2>
          <button onClick={onClose} className="p-2 hover:bg-cream rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {step === 'upload' && (
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              className={cn(
                'border-2 border-dashed rounded-xl p-12 text-center transition-colors',
                error ? 'border-red-300 bg-red-50' : 'border-warm-gray/30 hover:border-sage'
              )}
            >
              <Upload className="w-12 h-12 mx-auto mb-4 text-warm-gray" />
              <p className="text-navy font-medium mb-2">Drop your CSV file here</p>
              <p className="text-sm text-warm-gray mb-4">or</p>
              <label className="inline-block px-6 py-2 bg-sage text-white rounded-lg cursor-pointer hover:bg-sage-dark transition-colors">
                Browse Files
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </label>
              {error && (
                <p className="mt-4 text-red-500 flex items-center justify-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </p>
              )}
              <div className="mt-6 text-left bg-cream rounded-lg p-4">
                <p className="text-sm font-medium text-navy mb-2">Expected CSV format:</p>
                <code className="text-xs text-warm-gray block">
                  date,category,description,amount,vendor,notes
                </code>
              </div>
            </div>
          )}

          {step === 'preview' && preview && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-cream rounded-lg text-center">
                  <p className="text-2xl font-bold text-navy">{preview.totalRows}</p>
                  <p className="text-sm text-warm-gray">Total Rows</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{preview.validRows}</p>
                  <p className="text-sm text-warm-gray">Valid</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-600">{preview.invalidRows}</p>
                  <p className="text-sm text-warm-gray">Invalid</p>
                </div>
              </div>

              {/* Options */}
              <div className="space-y-4 p-4 bg-cream rounded-lg">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={skipDuplicates}
                    onChange={(e) => setSkipDuplicates(e.target.checked)}
                    className="w-4 h-4 rounded text-sage focus:ring-sage"
                  />
                  <span className="text-navy">Skip duplicate entries</span>
                </label>
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">
                    Default category for unmatched entries
                  </label>
                  <select
                    value={defaultCategory}
                    onChange={(e) => setDefaultCategory(e.target.value as ExpenseCategory)}
                    className="w-full px-4 py-2 bg-white rounded-lg border border-warm-gray/20"
                  >
                    {Object.entries(EXPENSE_CATEGORIES).map(([value, config]) => (
                      <option key={value} value={value}>
                        {config.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Preview Table */}
              <div className="border border-warm-gray/10 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-cream-dark">
                    <tr>
                      <th className="px-4 py-2 text-left text-navy">#</th>
                      <th className="px-4 py-2 text-left text-navy">Date</th>
                      <th className="px-4 py-2 text-left text-navy">Description</th>
                      <th className="px-4 py-2 text-right text-navy">Amount</th>
                      <th className="px-4 py-2 text-center text-navy">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview.slice(0, 5).map((row) => (
                      <tr key={row.rowNumber} className="border-t border-warm-gray/10">
                        <td className="px-4 py-2 text-warm-gray">{row.rowNumber}</td>
                        <td className="px-4 py-2">{row.data.expenseDate}</td>
                        <td className="px-4 py-2">{row.data.description}</td>
                        <td className="px-4 py-2 text-right">${row.data.amount}</td>
                        <td className="px-4 py-2 text-center">
                          {row.isValid ? (
                            <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-red-500 mx-auto" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Errors */}
              {preview.errors.length > 0 && (
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="font-medium text-red-700 mb-2">Validation Errors:</p>
                  <ul className="text-sm text-red-600 space-y-1">
                    {preview.errors.slice(0, 5).map((err, i) => (
                      <li key={i}>
                        Row {err.rowNumber}: {err.field} - {err.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {step === 'result' && result && (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-navy mb-2">Import Complete!</h3>
              <p className="text-warm-gray mb-6">
                Successfully imported {result.imported} expense{result.imported !== 1 ? 's' : ''}.
                {result.skipped > 0 && ` Skipped ${result.skipped} duplicate(s).`}
              </p>
              <button
                onClick={onClose}
                className="px-6 py-3 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
              >
                Done
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 'preview' && (
          <div className="px-6 py-4 border-t border-warm-gray/10 flex justify-between">
            <button
              onClick={handleReset}
              className="px-4 py-2 text-navy hover:bg-cream rounded-lg transition-colors"
            >
              Back
            </button>
            <button
              onClick={handleConfirm}
              disabled={isLoading || (preview?.validRows || 0) === 0}
              className="px-6 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Importing...' : `Import ${preview?.validRows} Expenses`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ExpenseImportModal;
